import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { makeApi } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { BrokerDialog } from "@/components/brokers/BrokerDialog";

const missing = (b) => !b.address;

export default function Brokers() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [brokers, setBrokers] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const refresh = () => api.get("/brokers").then(({ data }) => setBrokers(data)).catch(() => { setBrokers([]); toast.error("Could not load brokers"); });
  useEffect(() => { refresh(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = (brokers || []).filter((b) => !q.trim() || [b.name, b.contact_name, b.email, b.mc_number].some((v) => (v || "").toLowerCase().includes(q.trim().toLowerCase())));
  const remove = async () => {
    try { await api.delete(`/brokers/${removing.id}`); toast.success("Broker deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    setRemoving(null);
  };

  return (
    <div className="space-y-6" data-testid="brokers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Brokers</h1>
          <p className="text-sm text-slate-500 mt-1">Your customers. Their saved details fill the bill-to block on every invoice.</p>
        </div>
        <Button onClick={() => setEditing({})} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="add-broker-btn"><Plus className="w-4 h-4 mr-1" /> Add broker</Button>
      </div>
      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 relative">
          <Search className="w-4 h-4 absolute left-7 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, contact, email or MC…" className="pl-9" data-testid="broker-search-input" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>{["Broker", "Billing address", "Contact", "MC", "Loads", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {brokers === null && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {brokers && shown.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="brokers-empty"><Building2 className="w-6 h-6 mx-auto mb-2" />{q ? "No brokers match." : "No brokers yet."}</td></tr>}
              {shown.map((b) => (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setEditing(b)} data-testid={`broker-row-${b.id}`}>
                  <td className="px-4 py-3 font-semibold">{b.name}{b.notes && <div className="text-xs font-normal text-slate-500 truncate max-w-[220px]">{b.notes}</div>}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-pre-line">{b.address || <span className="pill whitespace-nowrap bg-amber-50 text-amber-800 border border-amber-200" data-testid={`broker-missing-${b.id}`}>Add billing address</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{[b.contact_name, b.phone, b.email].filter(Boolean).map((v) => <div key={v}>{v}</div>)}</td>
                  <td className="px-4 py-3 text-slate-600">{b.mc_number}</td>
                  <td className="px-4 py-3 tabular-nums">{b.load_count}{b.trash_count > 0 && <span className="text-xs text-slate-400"> (+{b.trash_count} in Trash)</span>}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => setEditing(b)} aria-label={`Edit ${b.name}`} data-testid={`broker-edit-${b.id}`}><Pencil className="w-4 h-4" /></Button>
                    {b.load_count === 0 && !b.trash_count && <Button variant="ghost" size="icon" onClick={() => setRemoving(b)} className="text-slate-400 hover:text-rose-600" aria-label={`Delete ${b.name}`} data-testid={`broker-delete-${b.id}`}><Trash2 className="w-4 h-4" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {brokers?.some(missing) && <p className="px-4 py-3 text-xs text-amber-800 border-t border-slate-100">Brokers without a billing address show only their name on invoices.</p>}
      </Card>
      <BrokerDialog broker={editing} api={api} onClose={() => setEditing(null)} onSaved={refresh} />
      <ConfirmDialog open={!!removing} title={`Delete ${removing?.name}?`} description="This broker has no loads. You can add them again anytime." onConfirm={remove} onCancel={() => setRemoving(null)} testId="broker-delete-confirm" />
    </div>
  );
}
